import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { IReportQuery } from "../../models/report/query";
import { IReport } from "../../models/report/response";
import {
  ICreateExternalBillingReport,
  ICreatePurchaseForecastReport,
  ICreateReport,
} from "../../models/report/request";

class repotservice<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  private getDownloadPath = (downloadUrl: string) => {
    const rawUrl = downloadUrl.trim();
    const apiBaseUrl = process.env.REACT_APP_BASE_API_URL;

    if (!apiBaseUrl) {
      return rawUrl.startsWith("http") || rawUrl.startsWith("/")
        ? rawUrl
        : `/${rawUrl}`;
    }

    try {
      const baseUrl = new URL(apiBaseUrl);
      const candidateUrl =
        /^https?:\/\//i.test(rawUrl) || rawUrl.startsWith("/")
          ? rawUrl
          : `/${rawUrl}`;
      const url = new URL(candidateUrl, baseUrl);

      if (url.origin !== baseUrl.origin) return rawUrl;

      const basePath = baseUrl.pathname.replace(/\/$/, "");
      const path = url.pathname.startsWith(`${basePath}/`)
        ? url.pathname.slice(basePath.length)
        : url.pathname;

      return `${path || "/"}${url.search}`;
    } catch {
      return rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    }
  };

  getAllReports = (query: IReportQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IReport>>({
      path: `/report`,
      method: "GET",
      query,
      ...params,
    });

  createReport = (data: ICreateReport, path: string, params?: RequestParams) =>
    this.http.request<IReport>({
      path: path,
      method: "POST",
      body: data,
      ...params,
    });

  createLowStockReport = (data: ICreateReport, params?: RequestParams) =>
    this.http.request<IReport>({
      path: `/export-low-stock-report`,
      method: "POST",
      body: data,
      ...params,
    });

  createPurchaseForecastReport = (
    data: ICreatePurchaseForecastReport,
    params?: RequestParams,
  ) =>
    this.http.request<IReport>({
      path: `/export-purchase-forecast`,
      method: "POST",
      body: data,
      ...params,
    });

  getReport = (id: number, params?: RequestParams) =>
    this.http.request<IReport>({
      path: `/report/${id}`,
      method: "GET",
      ...params,
    });

  createExternalBillingReport = (
    data: ICreateExternalBillingReport,
    params?: RequestParams,
  ) =>
    this.http.request<IReport>({
      path: "/export-external-billing",
      method: "POST",
      body: data,
      ...params,
    });

  confirmExternalBillingReport = (id: number, params?: RequestParams) =>
    this.http.request<IReport>({
      path: `/report/${id}/confirm-external-billing`,
      method: "POST",
      ...params,
    });

  retryExternalBillingReport = (id: number, params?: RequestParams) =>
    this.http.request<IReport>({
      path: `/report/${id}/retry-external-billing`,
      method: "POST",
      ...params,
    });

  downloadReportById = (id: number, params?: RequestParams) =>
    this.http.request<Blob>({
      path: `/report/${id}/download`,
      method: "GET",
      format: "blob",
      headers: { Accept: "application/octet-stream" },
      ...params,
    });

  downloadReport = (downloadUrl: string, params?: RequestParams) =>
    this.http.request<Blob>({
      path: this.getDownloadPath(downloadUrl),
      method: "GET",
      format: "blob",
      headers: {
        Accept: "application/octet-stream",
      },
      ...params,
    });
}

export default repotservice;
